import { Button, ChevronDownIcon, DropdownMenu, IconSize, Tooltip } from '@protoss/ui';

import { STATIC_SOW_WIZARD_CATEGORY_KEYS, useDealWorkspace } from '../dealWorkspaceContext';

type Artifact = 'estimate' | 'resource-plan' | 'sow';

/**
 * Unified generate/regenerate control for the deal document tabs - one "Regenerate v"
 * dropdown that mirrors the Export menu, replacing the side-by-side "Regenerate all" +
 * per-artifact buttons. The menu lists the per-artifact regenerations (estimate incl.
 * resource plan, SOW) with the CURRENT tab's artifact floated to the top, then a separator
 * and "Regenerate all". Each item routes through the same confirm dialogs + handlers as
 * before (overrides-aware). Reads everything from {@link useDealWorkspace}.
 *
 * Before anything is generated only "Generate all" is meaningful, so it renders as a plain
 * button (a one-item dropdown would be pointless) - this is how the questionnaire-header
 * instance looks for a brand-new deal until the first generation completes.
 */
export function RegenerateMenuButton({ currentArtifact }: { currentArtifact?: Artifact }) {
  const {
    deal,
    isGenerating,
    hasEstimateResult,
    hasSavedEstimate,
    hasSavedSow,
    isSowSupportedForDeal,
    generateButtonDisabled,
    generateDisabledReasons,
    isAiRunActive,
    handleGenerateAll,
    handleRegenerateEstimate,
    handleRegenerateSow,
    openRegenerateConfirm,
    openEstimateRegenerateConfirm,
    openSowRegenerateConfirm,
  } = useDealWorkspace();

  const disabled = generateButtonDisabled || isAiRunActive;
  // Every reason the control is disabled, shown on hover (one line each). Nothing to explain
  // while this deal is generating - the button already says "Generating...".
  const reasons =
    !disabled || isGenerating
      ? []
      : [
          ...(isAiRunActive
            ? [{ text: 'Another AI task is running. Wait for it to finish.' }]
            : []),
          ...generateDisabledReasons,
        ];
  const disabledReason =
    reasons.length === 0 ? null : (
      <div className="regenerate-menu__reasons">
        <div className="regenerate-menu__reasons-title">
          {reasons.length === 1
            ? "Why you can't generate yet"
            : `Why you can't generate yet (${reasons.length} reasons)`}
        </div>
        <ul className="regenerate-menu__reasons-list">
          {reasons.map((r) => (
            <li key={r.text}>
              {r.text}
              {r.details && r.details.length > 0 ? (
                <ul className="regenerate-menu__reasons-details">
                  {r.details.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    );

  // Static-SOW categories (managed_services): the SOW is generated deterministically
  // from the contract terms (Generate SOW on the Contract tab), never as part of a
  // chat generation chain - so "Generate all" is meaningless here and the control
  // collapses to an estimate-only button. Regenerating the estimate re-fills the
  // contract form's estimate-derived fields.
  if (deal?.dealCategoryKey != null && STATIC_SOW_WIZARD_CATEGORY_KEYS.has(deal.dealCategoryKey)) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() =>
                hasSavedEstimate ? openEstimateRegenerateConfirm() : handleRegenerateEstimate()
              }
            >
              {isGenerating
                ? 'Generating...'
                : hasEstimateResult
                  ? 'Regenerate estimate'
                  : 'Generate estimate'}
            </Button>
          </span>
        </Tooltip.Trigger>
        {disabledReason && (
          <Tooltip.Portal>
            <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
              {disabledReason}
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    );
  }

  type Item = { key: Artifact | 'all'; label: string; onSelect: () => void };

  // Per-artifact regenerations. The resource plan has no standalone action - it is rebuilt
  // by "Regenerate estimate" (the estimate regen includes the resource plan).
  const perArtifact: Item[] = [];
  if (hasEstimateResult) {
    perArtifact.push({
      key: 'estimate',
      label: 'Regenerate estimate',
      onSelect: () =>
        hasSavedEstimate ? openEstimateRegenerateConfirm() : handleRegenerateEstimate(),
    });
  }
  // The SOW item's only precondition is a saved estimate to read (the SOW-only run
  // never touches the estimate) - so it appears as "Generate SOW" before the first
  // SOW exists. The overwrite-confirm dialog is only meaningful once a SOW is saved;
  // a first generation runs directly, like the estimate item.
  if (isSowSupportedForDeal && (hasSavedSow || hasSavedEstimate)) {
    perArtifact.push({
      key: 'sow',
      label: hasSavedSow ? 'Regenerate SOW' : 'Generate SOW',
      onSelect: () => (hasSavedSow ? openSowRegenerateConfirm() : handleRegenerateSow()),
    });
  }
  // Float the current tab's artifact to the top (the resource-plan tab maps to 'estimate').
  const currentKey: Item['key'] | undefined =
    currentArtifact === 'resource-plan' ? 'estimate' : currentArtifact;
  perArtifact.sort((a, b) => (a.key === currentKey ? -1 : b.key === currentKey ? 1 : 0));

  const allItem: Item = {
    key: 'all',
    label: hasEstimateResult ? 'Regenerate all' : 'Generate all',
    onSelect: () => (hasSavedEstimate ? openRegenerateConfirm() : handleGenerateAll()),
  };

  // Nothing generated yet -> only "Generate all" makes sense; render a plain button.
  if (perArtifact.length === 0) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => allItem.onSelect()}
            >
              {isGenerating ? 'Generating...' : 'Generate all'}
            </Button>
          </span>
        </Tooltip.Trigger>
        {disabledReason && (
          <Tooltip.Portal>
            <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
              {disabledReason}
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    );
  }

  const triggerLabel = isGenerating
    ? 'Generating...'
    : hasEstimateResult
      ? 'Regenerate'
      : 'Generate';

  // A disabled trigger won't open the menu, so it shows the reasons on hover instead.
  const trigger = (
    <Button type="button" variant="outline" size="sm" disabled={disabled}>
      {triggerLabel}
      <ChevronDownIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
    </Button>
  );
  if (disabledReason) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span>{trigger}</span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
            {disabledReason}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
          {perArtifact.map((it) => (
            <DropdownMenu.Item
              key={it.key}
              className="dropdown-item"
              onSelect={() => it.onSelect()}
            >
              {it.label}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="dropdown-separator" />
          <DropdownMenu.Item className="dropdown-item" onSelect={() => allItem.onSelect()}>
            {allItem.label}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
